import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  HostListener,
  OnDestroy,
  QueryList,
  ViewChild,
  ViewChildren,
  afterEveryRender,
  computed,
  effect,
  input,
  output,
  signal
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MButtonComponent } from '@design-system/components/m-button';
import { MCalendarComponent } from '@design-system/components/m-calendar';
import { MIconFieldComponent } from '@design-system/components/m-icon-field/m-icon-field.component';
import { MOverlayPanelComponent } from '@design-system/components/m-overlaypanel';
import { DatePicker } from 'primeng/datepicker';
import { MPeriodPickerComponent } from './m-period-picker.component';
import {
  ComparePeriodGranularity,
  ComparePeriodSelection,
  DatePeriod,
  PeriodPickerView
} from './m-compare-period-picker.types';
import {
  addDays,
  clampPanelToViewport,
  formatCompareTriggerLabel,
  formatIsoDate,
  isPeriodAllowed,
  isPeriodSelectable,
  parseIsoDate,
  weekPeriodContaining
} from './m-compare-period-picker.utils';

/** Sticky app header selectors, most specific first (wrapper includes alert banner). */
const STICKY_HEADER_SELECTORS = ['.sticky-top', '.nav-bar'];

interface PendingComparePeriodSelection {
  granularity: ComparePeriodGranularity;
  current: DatePeriod | null;
  comparison: DatePeriod | null;
}

function cloneSelection(
  selection: ComparePeriodSelection
): PendingComparePeriodSelection {
  return {
    granularity: selection.granularity,
    current: { ...selection.current },
    comparison: { ...selection.comparison }
  };
}

function isCompletePeriod(
  period: DatePeriod | null | undefined
): period is DatePeriod {
  return !!(period?.startDate && period?.endDate);
}

const GRANULARITY_TABS: { label: string; value: ComparePeriodGranularity }[] = [
  { label: 'Weekly', value: 'week' },
  { label: 'Monthly', value: 'month' },
  { label: 'Quarterly', value: 'quarter' },
  { label: 'Yearly', value: 'year' }
];

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'm-compare-period-picker',
  templateUrl: './m-compare-period-picker.component.html',
  styleUrls: ['./m-compare-period-picker.component.scss'],
  imports: [
    FormsModule,
    MButtonComponent,
    MCalendarComponent,
    MIconFieldComponent,
    MOverlayPanelComponent,
    MPeriodPickerComponent
  ]
})
export class MComparePeriodPickerComponent implements OnDestroy {
  @ViewChild('panel') panel!: MOverlayPanelComponent;
  @ViewChild('trigger', { read: ElementRef })
  trigger!: ElementRef<HTMLElement>;
  @ViewChildren(MCalendarComponent)
  private weekCalendars!: QueryList<MCalendarComponent>;

  private rightAlignHooked = false;
  private panelResizeObserver: ResizeObserver | null = null;
  private readonly weekNavPatched = new WeakSet<MCalendarComponent>();

  /** Max height (px) for the panel body so it never runs under the nav or off the viewport. */
  readonly panelMaxHeight = signal<number | null>(null);

  readonly value = input.required<ComparePeriodSelection>();
  readonly granularities = input<ComparePeriodGranularity[]>([
    'week',
    'month',
    'quarter',
    'year'
  ]);
  readonly currentLabel = input('Current Time Period');
  readonly comparisonLabel = input('Comparison Time Period');
  /** Inclusive lower bound for current pane (YYYY-MM-DD). */
  readonly currentMinDate = input<string | null>(null);
  /** Inclusive upper bound for current pane (YYYY-MM-DD). */
  readonly currentMaxDate = input<string | null>(null);
  /** Inclusive lower bound for comparison pane (YYYY-MM-DD). */
  readonly comparisonMinDate = input<string | null>(null);
  /** Inclusive upper bound for comparison pane (YYYY-MM-DD). */
  readonly comparisonMaxDate = input<string | null>(null);
  /**
   * When set, only these periods are selectable. `null` / `[]` disables
   * date selection and Save (e.g. while periods are loading or empty).
   * Omit (`undefined`) to keep bounds-only behaviour.
   */
  readonly allowedPeriods = input<DatePeriod[] | null | undefined>(undefined);
  /**
   * When true, comparison selection cannot start on/after the pending current
   * period start (in addition to comparisonMaxDate).
   */
  readonly constrainComparisonToBeforeCurrent = input(true);
  readonly weekStartsOn = input(0);
  readonly disabled = input(false);

  readonly save = output<ComparePeriodSelection>();
  readonly cancelled = output<void>();
  readonly granularityChange = output<ComparePeriodGranularity>();

  readonly pending = signal<PendingComparePeriodSelection | null>(null);
  private readonly isOpen = signal(false);

  constructor() {
    // PrimeNG ignores min/max for month arrows — clamp weekly calendars here.
    afterEveryRender({
      write: () => this.syncWeekCalendarNavBounds()
    });
  }

  private readonly syncPendingFromValue = effect(() => {
    const applied = this.value();
    if (!this.isOpen()) {
      this.pending.set(cloneSelection(applied));
    }
  });

  readonly tabs = computed(() =>
    GRANULARITY_TABS.filter((tab) => this.granularities().includes(tab.value))
  );

  readonly triggerLabel = computed(() => {
    const selection = this.value();
    return formatCompareTriggerLabel(
      selection.granularity,
      selection.current,
      selection.comparison
    );
  });

  readonly activeGranularity = computed(
    () => this.pending()?.granularity ?? this.value().granularity
  );

  readonly periodView = computed((): PeriodPickerView | null => {
    const granularity = this.activeGranularity();
    if (granularity === 'week') return null;
    return granularity;
  });

  readonly pendingCurrent = computed(() => this.pending()?.current ?? null);
  readonly pendingComparison = computed(
    () => this.pending()?.comparison ?? null
  );

  readonly effectiveComparisonMaxDate = computed((): string | null => {
    const maxDate = this.comparisonMaxDate();
    if (!this.constrainComparisonToBeforeCurrent()) {
      return maxDate;
    }
    const currentStart = this.pending()?.current?.startDate;
    if (!currentStart) return maxDate;
    const dayBefore = formatIsoDate(addDays(parseIsoDate(currentStart), -1));
    if (!maxDate || dayBefore < maxDate) {
      return dayBefore;
    }
    return maxDate;
  });

  readonly canSave = computed(() => {
    const pending = this.pending();
    if (!pending) return false;
    if (!this.selectionEnabled()) return false;
    const { current, comparison } = pending;
    if (!isCompletePeriod(current) || !isCompletePeriod(comparison)) {
      return false;
    }
    if (
      !isPeriodSelectable(
        current,
        {
          minDate: this.currentMinDate(),
          maxDate: this.currentMaxDate()
        },
        this.allowedPeriods()
      )
    ) {
      return false;
    }
    if (
      !isPeriodSelectable(
        comparison,
        {
          minDate: this.comparisonMinDate(),
          maxDate: this.effectiveComparisonMaxDate()
        },
        this.allowedPeriods()
      )
    ) {
      return false;
    }
    if (
      this.constrainComparisonToBeforeCurrent() &&
      comparison.endDate >= current.startDate
    ) {
      return false;
    }
    return true;
  });

  /** False while allowlist is loading (`null`) or empty. */
  readonly selectionEnabled = computed(() => {
    const allowed = this.allowedPeriods();
    if (allowed === undefined) return true;
    return !!allowed && allowed.length > 0;
  });

  readonly weekDisabledDates = computed((): Date[] => {
    const allowed = this.allowedPeriods();
    if (!allowed || allowed.length === 0) {
      return [];
    }

    const starts = new Set(allowed.map((period) => period.startDate));
    const minIso = allowed.reduce(
      (min, period) => (period.startDate < min ? period.startDate : min),
      allowed[0]!.startDate
    );
    const maxIso = allowed.reduce(
      (max, period) => (period.endDate > max ? period.endDate : max),
      allowed[0]!.endDate
    );
    const weekStartsOn = this.weekStartsOn();
    const disabled: Date[] = [];
    for (
      let day = parseIsoDate(minIso);
      formatIsoDate(day) <= maxIso;
      day = addDays(day, 1)
    ) {
      const week = weekPeriodContaining(day, weekStartsOn);
      if (!starts.has(week.startDate)) {
        disabled.push(new Date(day));
      }
    }
    return disabled;
  });

  readonly currentWeekDates = computed((): Date[] | null => {
    const period = this.pendingCurrent();
    if (!period) return null;
    return [parseIsoDate(period.startDate), parseIsoDate(period.endDate)];
  });

  readonly comparisonWeekDates = computed((): Date[] | null => {
    const period = this.pendingComparison();
    if (!period) return null;
    return [parseIsoDate(period.startDate), parseIsoDate(period.endDate)];
  });

  /**
   * When comparison is cleared, PrimeNG falls back to today unless defaultDate
   * is set — keep the grid on the pending current month instead.
   */
  readonly comparisonCalendarDefaultDate = computed((): Date | undefined => {
    const current = this.pendingCurrent();
    if (!current) return undefined;
    return parseIsoDate(current.startDate);
  });

  readonly currentCalendarMin = computed(() =>
    this.toOptionalDate(
      this.restrictiveMinIso(this.currentMinDate(), this.allowedEnvelopeMin())
    )
  );
  readonly currentCalendarMax = computed(() =>
    this.toOptionalDate(
      this.restrictiveMaxIso(this.currentMaxDate(), this.allowedEnvelopeMax())
    )
  );
  readonly comparisonCalendarMin = computed(() =>
    this.toOptionalDate(
      this.restrictiveMinIso(
        this.comparisonMinDate(),
        this.allowedEnvelopeMin()
      )
    )
  );
  readonly comparisonCalendarMax = computed(() =>
    this.toOptionalDate(
      this.restrictiveMaxIso(
        this.effectiveComparisonMaxDate(),
        this.allowedEnvelopeMax()
      )
    )
  );

  openPanel(event: Event): void {
    if (this.disabled()) return;
    event.stopPropagation();
    this.isOpen.set(true);
    this.pending.set(cloneSelection(this.value()));
    this.ensureRightAlignHook();
    this.panel.showPanel(event);
  }

  ngOnDestroy(): void {
    this.teardownPanelResizeObserver();
  }

  /**
   * Prime left-aligns; shift so panel right matches trigger right, and keep
   * the panel below the sticky header / inside the viewport vertically.
   * Kept in sync via popover.align hook + ResizeObserver (Weekly size settle).
   * Restore: /tmp/m-compare-period-picker-align-ok-20260903-132938
   */
  alignPanelToTriggerEnd(): void {
    this.observePanelSize();
    this.repositionPanel();
  }

  @HostListener('window:resize')
  onWindowResize(): void {
    if (this.isOpen()) this.repositionPanel();
  }

  onPanelHide(): void {
    this.teardownPanelResizeObserver();
    this.isOpen.set(false);
    this.panelMaxHeight.set(null);
    this.pending.set(cloneSelection(this.value()));
    this.granularityChange.emit(this.value().granularity);
  }

  onGranularityChange(granularity: ComparePeriodGranularity): void {
    const current = this.pending();
    if (!current || current.granularity === granularity) return;

    // Returning to the saved granularity restores its periods so Weekly
    // re-anchors on the applied weeks instead of an empty "today" month.
    const applied = this.value();
    if (applied.granularity === granularity) {
      this.pending.set(cloneSelection(applied));
    } else {
      this.pending.set({
        granularity,
        current: null,
        comparison: null
      });
    }
    this.granularityChange.emit(granularity);
    this.alignPanelToTriggerEnd();
  }

  private ensureRightAlignHook(): void {
    if (this.rightAlignHooked) return;
    const popover = this.panel.popover;
    const originalAlign = popover.align.bind(popover);
    popover.align = () => {
      originalAlign();
      this.observePanelSize();
      this.repositionPanel();
    };
    this.rightAlignHooked = true;
  }

  private observePanelSize(): void {
    const container = this.panel?.popover?.container as HTMLElement | null;
    if (!container || typeof ResizeObserver === 'undefined') return;
    if (this.panelResizeObserver) return;

    this.panelResizeObserver = new ResizeObserver(() => {
      this.repositionPanel();
    });
    this.panelResizeObserver.observe(container);
  }

  private teardownPanelResizeObserver(): void {
    this.panelResizeObserver?.disconnect();
    this.panelResizeObserver = null;
  }

  private repositionPanel(): void {
    this.shiftPanelToTriggerEnd();
    this.clampPanelIntoViewport();
  }

  /**
   * PrimeNG flips the panel above the trigger on short viewports and clamps it
   * to the viewport top, where the sticky nav hides the granularity tabs. Pin
   * the panel below the header and cap its height so it scrolls internally.
   */
  private clampPanelIntoViewport(): void {
    const container = this.panel?.popover?.container as HTMLElement | null;
    if (!container || !this.isOpen()) return;

    const panelHeight = container.offsetHeight;
    if (!panelHeight) return;

    const scrollY =
      window.pageYOffset || document.documentElement.scrollTop || 0;
    const styledTop = parseFloat(container.style.top);
    const panelTop = Number.isFinite(styledTop)
      ? styledTop
      : container.getBoundingClientRect().top + scrollY;

    const { top, maxHeight } = clampPanelToViewport({
      panelTop,
      panelHeight,
      viewportHeight: window.innerHeight,
      scrollY,
      occludedTop: this.stickyHeaderBottom()
    });

    container.style.top = `${top}px`;
    this.panelMaxHeight.set(maxHeight);
  }

  /** Bottom edge (viewport px) of the sticky app header; 0 when none is rendered (e.g. Storybook). */
  private stickyHeaderBottom(): number {
    for (const selector of STICKY_HEADER_SELECTORS) {
      const header = document.querySelector<HTMLElement>(selector);
      if (header) return Math.max(0, header.getBoundingClientRect().bottom);
    }
    return 0;
  }

  private shiftPanelToTriggerEnd(): void {
    const container = this.panel?.popover?.container as HTMLElement | null;
    const target = this.trigger?.nativeElement;
    if (!container || !target || !this.isOpen()) return;

    const width = container.offsetWidth;
    if (!width) return;

    const triggerRight = target.getBoundingClientRect().right;
    const scrollX =
      window.pageXOffset || document.documentElement.scrollLeft || 0;
    const next = `${Math.max(0, triggerRight + scrollX - width)}px`;
    container.style.insetInlineStart = next;
    container.style.left = next;
  }

  onCurrentPeriodChange(period: DatePeriod): void {
    if (
      this.allowedPeriods() !== undefined &&
      !isPeriodAllowed(period, this.allowedPeriods())
    ) {
      return;
    }
    const current = this.pending();
    if (!current) return;
    const next: PendingComparePeriodSelection = {
      ...current,
      current: period
    };
    if (
      next.comparison &&
      !this.isComparisonValidAgainstCurrent(next.comparison, period)
    ) {
      next.comparison = null;
    }
    this.pending.set(next);
  }

  onComparisonPeriodChange(period: DatePeriod): void {
    if (
      this.allowedPeriods() !== undefined &&
      !isPeriodAllowed(period, this.allowedPeriods())
    ) {
      return;
    }
    const current = this.pending();
    if (!current) return;
    this.pending.set({ ...current, comparison: period });
  }

  onCurrentWeekSelect(clickedDate: Date): void {
    if (!this.selectionEnabled()) return;
    const period = weekPeriodContaining(clickedDate, this.weekStartsOn());
    const allowed = this.allowedPeriods();
    if (allowed !== undefined && !isPeriodAllowed(period, allowed)) return;
    this.onCurrentPeriodChange(period);
  }

  onComparisonWeekSelect(clickedDate: Date): void {
    if (!this.selectionEnabled()) return;
    const period = weekPeriodContaining(clickedDate, this.weekStartsOn());
    const allowed = this.allowedPeriods();
    if (allowed !== undefined && !isPeriodAllowed(period, allowed)) return;
    this.onComparisonPeriodChange(period);
  }

  onCancel(): void {
    this.pending.set(cloneSelection(this.value()));
    this.panel.hidePanel();
    this.isOpen.set(false);
    this.cancelled.emit();
  }

  onSave(): void {
    if (!this.canSave()) return;
    const selection = this.pending();
    if (
      !selection ||
      !isCompletePeriod(selection.current) ||
      !isCompletePeriod(selection.comparison)
    ) {
      return;
    }
    this.save.emit({
      granularity: selection.granularity,
      current: { ...selection.current },
      comparison: { ...selection.comparison }
    });
    this.isOpen.set(false);
    this.panel.hidePanel();
  }

  private isComparisonValidAgainstCurrent(
    comparison: DatePeriod,
    current: DatePeriod
  ): boolean {
    if (
      this.constrainComparisonToBeforeCurrent() &&
      comparison.endDate >= current.startDate
    ) {
      return false;
    }

    let maxDate = this.comparisonMaxDate();
    if (this.constrainComparisonToBeforeCurrent()) {
      const dayBefore = formatIsoDate(
        addDays(parseIsoDate(current.startDate), -1)
      );
      if (!maxDate || dayBefore < maxDate) {
        maxDate = dayBefore;
      }
    }

    return isPeriodSelectable(comparison, {
      minDate: this.comparisonMinDate(),
      maxDate
    });
  }

  private toOptionalDate(iso: string | null | undefined): Date | undefined {
    return iso ? parseIsoDate(iso) : undefined;
  }

  /** Earliest startDate across the allowlist, or null when unused / empty. */
  private allowedEnvelopeMin(): string | null {
    const allowed = this.allowedPeriods();
    if (!allowed?.length) return null;
    return allowed.reduce(
      (min, period) => (period.startDate < min ? period.startDate : min),
      allowed[0]!.startDate
    );
  }

  /** Latest endDate across the allowlist, or null when unused / empty. */
  private allowedEnvelopeMax(): string | null {
    const allowed = this.allowedPeriods();
    if (!allowed?.length) return null;
    return allowed.reduce(
      (max, period) => (period.endDate > max ? period.endDate : max),
      allowed[0]!.endDate
    );
  }

  private restrictiveMinIso(
    input: string | null | undefined,
    envelope: string | null
  ): string | null {
    if (input && envelope) return input > envelope ? input : envelope;
    return input ?? envelope;
  }

  private restrictiveMaxIso(
    input: string | null | undefined,
    envelope: string | null
  ): string | null {
    if (input && envelope) return input < envelope ? input : envelope;
    return input ?? envelope;
  }

  private syncWeekCalendarNavBounds(): void {
    if (this.activeGranularity() !== 'week') return;
    this.weekCalendars?.forEach((calendar) => {
      this.ensureWeekCalendarNavPatched(calendar);
      this.syncCalendarNavButtons(calendar);
    });
  }

  private ensureWeekCalendarNavPatched(calendar: MCalendarComponent): void {
    if (this.weekNavPatched.has(calendar)) return;
    const picker = calendar.pCalendar;
    if (!picker) return;

    this.weekNavPatched.add(calendar);
    const originalBackward = picker.navBackward.bind(picker);
    const originalForward = picker.navForward.bind(picker);

    picker.navBackward = (event: Event) => {
      if (this.isPrevMonthNavDisabled(picker)) {
        event.preventDefault();
        return;
      }
      originalBackward(event);
      this.syncCalendarNavButtons(calendar);
    };

    picker.navForward = (event: Event) => {
      if (this.isNextMonthNavDisabled(picker)) {
        event.preventDefault();
        return;
      }
      originalForward(event);
      this.syncCalendarNavButtons(calendar);
    };
  }

  private isPrevMonthNavDisabled(picker: DatePicker): boolean {
    const min = picker.minDate;
    if (!min) return false;
    return (
      picker.currentYear < min.getFullYear() ||
      (picker.currentYear === min.getFullYear() &&
        picker.currentMonth <= min.getMonth())
    );
  }

  private isNextMonthNavDisabled(picker: DatePicker): boolean {
    const max = picker.maxDate;
    if (!max) return false;
    return (
      picker.currentYear > max.getFullYear() ||
      (picker.currentYear === max.getFullYear() &&
        picker.currentMonth >= max.getMonth())
    );
  }

  private syncCalendarNavButtons(calendar: MCalendarComponent): void {
    const picker = calendar.pCalendar;
    const host = picker?.el?.nativeElement as HTMLElement | undefined;
    if (!picker || !host) return;

    const prev = host.querySelector(
      '.p-datepicker-prev-button'
    ) as HTMLButtonElement | null;
    const next = host.querySelector(
      '.p-datepicker-next-button'
    ) as HTMLButtonElement | null;

    if (prev) {
      const disabled = this.isPrevMonthNavDisabled(picker);
      prev.disabled = disabled;
      prev.classList.toggle('p-disabled', disabled);
      prev.setAttribute('aria-disabled', String(disabled));
    }
    if (next) {
      const disabled = this.isNextMonthNavDisabled(picker);
      next.disabled = disabled;
      next.classList.toggle('p-disabled', disabled);
      next.setAttribute('aria-disabled', String(disabled));
    }
  }
}
